<script setup lang="ts">
import type { GCDComicDisplay } from '~/types/gcd'
import { useImage } from '@vueuse/core'

const props = defineProps<{
  comic: GCDComicDisplay
  loading: boolean
  error: boolean
  success: boolean
}>()

defineEmits(['show-info'])

const title = computed(() => {
  return props.comic.series_name ? `${props.comic.series_name} #${props.comic.number}` : props.comic.title
})

const normalizeCover = computed(() => {
  if (!props.comic || !props.comic.cover) return ''
  if (!props.comic.cover.includes('//img')) return props.comic.cover
  const u = new URL(props.comic.cover)
  u.pathname = u.pathname.replace(/\/{2,}/g, '/')
  return u.toString()
})

const normalizePageCount = computed(() => {
  if (!props.comic || !props.comic.page_count) return '?'
  const count = parseFloat(props.comic.page_count)
  return isNaN(count) ? props.comic.page_count : Math.trunc(count)
})

const normalizePrice = computed(() => {
  if (!props.comic || !props.comic.price) return undefined
  const price = props?.comic?.price?.split(';')[0]?.trim()

  // Match something like "0.35 USD" or "0.35" or "1.00 USD"
  const usdMatch = price?.match(/^(\d+\.?\d*)\s*(USD|\$)?$/i)
  if (usdMatch) {
    const val = parseFloat(usdMatch[1] ?? '')
    if (val > 0 && val < 1) {
      return `${Math.round(val * 100)}¢`
    }
    return `$${val}`
  }

  // Fallback: simple replacements to shorten
  return price?.replace(/USD/gi, '$').replace(/\s+/g, '')
})

const errorMessages = [
  'Well this is awkward... there\'s no comic data here. Maybe the API is having a moment?',
  'Uh-oh... I can\'t find any comic data right now. Did the API take the day off?',
  'Hmmm... looks like I have no comic data to show. Is the API down?',
]
function getRandomErrorMessage() {
  return errorMessages[Math.floor(Math.random() * errorMessages.length)]
}

const { isLoading: isImageLoading } = useImage(() => ({ src: normalizeCover.value }))

const imageError = ref<Map<string, boolean>>(new Map())
function onImageError(key: string) {
  imageError.value.set(key, true)
}
</script>

<template>
  <div
    class="card bg-neutral/75 rounded-none w-full aspect-4/6 max-h-[225px] md:max-h-[600px] text-neutral-content"
    @click="$emit('show-info')"
  >
    <div
      class="card-body overflow-hidden"
      data-cy="comic-card"
    >
      <figure class="h-full w-full cursor-pointer">
        <div
          v-if="loading"
          class="loading loading-spinner loading-xl text-base-100"
          data-cy="comic-loading"
        />
        <div v-else-if="error && !comic.forceShow">
          <div
            role="alert"
            class="bg-error rounded text-error-content w-full p-1 text-xs md:text-sm lg:text-base flex flex-col justify-center items-center lg:h-72"
          >
            <Icon
              name="game-icons:surprised-skull"
              class=" size-12! md:size-4! lg:size-32!"
            />
            <div>{{ getRandomErrorMessage() }}</div>
          </div>
        </div>
        <div
          v-else-if="isImageLoading"
          class="loading loading-spinner loading-xl text-base-100"
        />
        <img
          v-else-if="!imageError.get(comic.api_url) && (success || (!loading && comic.forceShow))"
          :key="comic.api_url"
          :src="normalizeCover"
          :alt="title ?? ''"
          class="size-full object-contain"
          referrerpolicy="no-referrer"
          data-cy="comic-image"
          @error="onImageError(comic.api_url)"
        >
        <LandingMissingCover
          v-else
          :title="comic?.series_name ?? comic?.title ?? ''"
          :price="normalizePrice"
          :page-count="normalizePageCount"
          :publication-date="comic?.publication_date ?? ''"
        />
      </figure>
    </div>
  </div>
</template>
